import CombinedRange from 'common/filters/combined-range';
import InputFilter from 'common/filters/input-filter';
import PeopleFilter from 'common/filters/people-filter';
import RangeFilter from 'common/filters/range-filter';
import ValueFilter from 'common/filters/value-filter';
import cloneDeep from 'lodash.clonedeep';
import PropTypes from 'prop-types';
import React from 'react';
import { Div } from 'ui';
import { searchOperators } from '../../utils/app-configs';
import { determineOperatorAndValue } from '../../utils/helpers';
import EntityFilter from './entity-filter/containers/entity-filter-container';
import GeoFilter from './geo-filter/components/geo-filter';
import OperatorFilter from './operator-filter';

const getTooltipContent = (data = []) => data?.map(({ title, description }, i) => `
    <div style="margin-bottom: 15px;" key=${i}>
      <b>${title}</b>
      <div>
        ${description}
      </div>
    </div>
  `).join('');

const Filters = React.forwardRef((props, ref) => {
  const onRangeFilterChange = ({ filter, values }) => {
    const { updateFilters, activeFilters } = props;
    const filters = { ...activeFilters, [filter]: values };
    updateFilters(filters);
  };

  // eslint-disable-next-line
  const onEntityFilterChange = ({ filter, value }) => {
    const { updateFilters, activeFilters } = props;

    if (!value?.length) {
      const newActiveFilters = cloneDeep(activeFilters);
      delete newActiveFilters[filter];
      updateFilters({ ...newActiveFilters });
    } else {
      updateFilters({ ...activeFilters, [filter]: [...value] });
    }
  };

  const onValueFilterChange = ({ filter, value = null, checked = null, operator }) => {
    const { updateFilters, activeFilters } = props;
    let filters = { ...activeFilters };
    // if there is no active filter for this valueFilter, create one.
    if (!filters[filter]) {
      filters = { ...filters, [filter]: [] };
    }
    // When there is a operator you will receive data with the short hand
    // first we have to check what the operator and value is.Then we reformat it for later use
    let currentFilters = filters[filter];
    if (operator && filters[filter]?.length) {
      const determinedOperatorAndValue = determineOperatorAndValue(filters[filter].join(';'));
      currentFilters = [determinedOperatorAndValue.value];
    }

    // if the filter value is checked
    if (checked) {
      // add an extra filer value.
      filters = { ...filters, [filter]: [...currentFilters, value] };
    } else {
      // remove the filter value
      let newValues = currentFilters?.filter(v => v !== value);
      if (operator && currentFilters[0]) {
        newValues = currentFilters[0]?.split(';')?.filter(v => v !== value);
      }
      filters = { ...filters, [filter]: newValues };
    }

    // When you are done with adding/removing items.
    // You have to reapply the short hand on the filter that has an operator.
    if (operator && filters[filter]?.length) {
      let operatorValue = '';
      operatorValue = filters[filter].join(';');
      operatorValue = operator.shortHand.replace(/{value}/, operatorValue);
      filters = { ...filters, [filter]: [operatorValue] };
    }
    updateFilters(filters);
  };

  const onSearchFilterChange = ({ filter, value }) => {
    const { activeFilters, updateFilters } = props;
    if (!value || !value[0]) {
      const newActiveFilters = cloneDeep(activeFilters);
      delete newActiveFilters[filter];
      updateFilters({ ...newActiveFilters });
    } else {
      updateFilters({ ...activeFilters, [filter]: value });
    }
  };

  const onOperatorFilterChange = ({ filter, value }) => {
    const { activeFilters, updateFilters } = props;
    if (!value || !value[0]) {
      const newActiveFilters = cloneDeep(activeFilters);
      delete newActiveFilters[filter];
      updateFilters({ ...newActiveFilters });
    } else {
      updateFilters({ ...activeFilters, [filter]: value });
    }
  };

  const onGeoFilterChange = ({ filter, value }) => {
    const { updateFilters, activeFilters } = props;
    if (!value?.length) {
      const newActiveFilters = cloneDeep(activeFilters);
      delete newActiveFilters[filter];
      updateFilters({ ...newActiveFilters });
    } else {
      updateFilters({ ...activeFilters, [filter]: [...value] });
    }
  };

  const renderFilter = (filter) => {
    const { disabled, activeFilters } = props;
    if (filter?.isHidden) {
      return null;
    }
    if (filter.type === 'options' || filter.type === 'sectionedOptions') {
      return (
        <ValueFilter
          activeFilters={activeFilters[filter.id]}
          description={filter.description}
          options={filter.options}
          operators={filter.operators || []}
          onChange={({ value, checked, operator }) =>
            onValueFilterChange({ filter: filter.id, value, checked, operator })
          }
          disabled={disabled}
          hasCategories={filter.type === 'sectionedOptions'}
          showCount={filter.showCount}
          info={getTooltipContent(filter.filterInformation)}
        />
      );
    }

    if (filter.type === 'geoPolygon') {
      return <GeoFilter
        activeFilters={activeFilters[filter.id]}
        description={filter.description}
        timeRanges={filter.timeRanges}
        options={filter.options}
        addressOptions={filter.addressOptions}
        onChange={value =>
          onGeoFilterChange({ filter: filter.id, value })
        }
        disabled={disabled}
        info={getTooltipContent(filter.filterInformation)}
      />;
    }

    if (filter.type === 'combinedRange') {
      return (
        <CombinedRange
          filters={filter}
          relativeTimingRangeOptions={filter.rangeFixPoints}
          activeFilters={activeFilters}
          disabled={disabled}
          onChange={(filterId, values) =>
            onRangeFilterChange({ filter: filterId, values })
          }
          info={getTooltipContent(filter.filterInformation)}
        />
      );
    }

    if (filter.type === 'range') {
      return (
        <RangeFilter
          description={filter.description}
          min={filter.min}
          max={filter.max}
          unit={filter.unit}
          activeFilters={activeFilters[filter.id]}
          onChange={values => onRangeFilterChange({ filter: filter.id, values })}
          disabled={disabled}
          allowClear={true}
          info={getTooltipContent(filter.filterInformation)}
        />
      );
    }

    if (filter.type === 'search' || filter.type === 'freeText') {
      if (filter.id === 'installedAtPostalCode') {
        return (
          <OperatorFilter
            description={filter.description}
            activeFilters={activeFilters[filter.id]}
            operators={filter.operators}
            onChange={value => onOperatorFilterChange({ filter: filter.id, value })}
            defaultOperator={searchOperators.startsWith.id}
            prefix='pc'
            info={getTooltipContent(filter.filterInformation)}
          />
        );
      }
      return (
        <InputFilter
          description={filter.description}
          activeFilters={activeFilters[filter.id]}
          onChange={value => onSearchFilterChange({ filter: filter.id, value })}
          info={getTooltipContent(filter.filterInformation)}
        />
      );
    }

    if (filter.type === 'entity' || filter.type === 'multiEntity') {
      const showChildrenOperator = filter.operators?.length &&
        filter.operators.find(o => o.id === 'showChildren');
      return (
        <EntityFilter
          description={filter.description}
          id={filter.id}
          type={filter.type}
          onChange={value => onEntityFilterChange({
            filter: filter.id,
            value: value ? [value] : [],
          })}
          activeOptions={filter.options}
          multiType={!!filter.types?.length}
          types={filter.types}
          filters={props.filters}
          sort={props.sort}
          worklistId={props.isTickets ? 'ticket' : props.worklistId}
          fixOptions={props.fixedFilterValues[filter.id] || []}
          info={getTooltipContent(filter.filterInformation)}
          operators={filter.operators}
          showChildren={activeFilters[filter.id]?.length && showChildrenOperator ?
            // eslint-disable-next-line max-len
            activeFilters[filter.id][0]?.toLowerCase()?.startsWith(showChildrenOperator?.description?.toLowerCase()) :
            false}
          isMultiEntity={filter.type === 'multiEntity'}
          currentFilterValue={activeFilters[filter.id]}
          dropdownMatchSelectWidth={filter.id === 'technician'}
        />
      );
    }

    if (filter.type === 'people' || filter.type === 'user') {
      return (
        <PeopleFilter
          description={filter.description}
          activeFilters={activeFilters[filter.id]}
          onChange={value => onSearchFilterChange({ filter: filter.id, value })}
          defaultMe={false}
          currentUserId={filter.userId}
          currentUserName={filter.userDisplayName}
          isRestricted={props.isTickets}
          info={getTooltipContent(filter.filterInformation)}
        />
      );
    }

    // This should not happen!!
    return <span>{`Could not render filter ${filter.id}`}</span>;
  };

  return (
    <Div ref={ref} {...props} data-test-id='filters.wrapper'>
      {props?.filterDefinitions?.sort((a, b) => a.sort - b.sort)?.map(f =>
        <Div marginBottom={20} key={f.id} data-test-id={`filter-item.${f.type}`}>
          {renderFilter(f)}
        </Div>,
      )}
    </Div>
  );
});

Filters.propTypes = {
  disabled: PropTypes.bool,
  filterDefinitions: PropTypes.array,
  innerRef: PropTypes.func,
  updateFilters: PropTypes.func,
  activeFilters: PropTypes.object,
  isPlanningBoard: PropTypes.bool,
  filterId: PropTypes.string,
  filters: PropTypes.string,
  sort: PropTypes.string,
  worklistId: PropTypes.string,
  isTickets: PropTypes.bool,
  relativeTimingRangeOptions: PropTypes.object,
  fixedFilterValues: PropTypes.array,
};

export default Filters;
